import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const ValuationScheduleDialog = ({
  row,
  open,
  onClose,
  examScheduleMasterId,
  examDate,
  programId,
  branchId,
  showSnackbar,
}) => {
  const [scheduleDetails, setScheduleDetails] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [form, setForm] = useState({
    college_id: row.college_id || "",
    exam_schedule_master_id: row.exam_schedule_master_id || "",
    valuation_type: "NORMAL",
    revaluation_round: 0,
    valuation_pattern_id: "",
    valuation_from_date: "",
    valuation_to_date: "",
    bundle_no_from: "",
    bundle_no_to: "",
    script_allot_size: "",
    workflow_status: "DRAFT",
  });
  const [editId, setEditId] = useState(null); // track edited row

  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fetchScheduleDetails = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/valuation-schedule/${clientId}/${collegeId}/${row.exam_schedule_master_id}`
      );
      setScheduleDetails(res.data || []);
    } catch (err) {
      console.error("Error fetching schedules:", err);
    }
  };

  useEffect(() => {
    if (open) fetchScheduleDetails();
  }, [open]);

  const handleClose = () => {
    onClose();
    setEditId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleAddOrUpdateSchedule = async () => {
    try {
      const payload = {
        ...form,
        revaluation_round: Number(form.revaluation_round),
        valuation_pattern_id: form.valuation_pattern_id ? Number(form.valuation_pattern_id) : null,
        bundle_no_from: form.bundle_no_from ? Number(form.bundle_no_from) : null,
        bundle_no_to: form.bundle_no_to ? Number(form.bundle_no_to) : null,
        script_allot_size: form.script_allot_size ? Number(form.script_allot_size) : null,
      };

      if (editId) {
        // Update existing
        await axios.put(
          `${API_BASE_URL}/api/valuation-schedule/${clientId}/${collegeId}/${examScheduleMasterId}/${editId}`,
          payload
        );
        alert("Valuation schedule updated successfully!");
      } else {
        // Add new
        await axios.post(
          `${API_BASE_URL}/api/valuation-schedule/${clientId}/${collegeId}/${examScheduleMasterId}`,
          payload
        );
        alert("New valuation schedule added successfully!");
      }

      setForm({
        college_id: row.college_id || "",
        exam_schedule_master_id: row.exam_schedule_master_id || "",
        valuation_type: "NORMAL",
        revaluation_round: 0,
        valuation_pattern_id: "",
        valuation_from_date: "",
        valuation_to_date: "",
        bundle_no_from: "",
        bundle_no_to: "",
        script_allot_size: "",
        workflow_status: "DRAFT",
      });

      setEditId(null);
      fetchScheduleDetails();
    } catch (err) {
      console.error("Error adding/updating schedule:", err);
      alert("Failed to save schedule!");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.valuation_schedule_master_id);
    setForm({
      college_id: row.college_id,
      exam_schedule_master_id: row.exam_schedule_master_id,
      valuation_type: row.valuation_type,
      revaluation_round: row.revaluation_round,
      valuation_pattern_id: row.valuation_pattern_id || "",
      valuation_from_date: row.valuation_from_date,
      valuation_to_date: row.valuation_to_date,
      bundle_no_from: row.bundle_no_from,
      bundle_no_to: row.bundle_no_to,
      script_allot_size: row.script_allot_size,
      workflow_status: row.workflow_status,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/valuation-schedule/${clientId}/${collegeId}/${examScheduleMasterId}/${id}`
      );
      alert("Valuation schedule deleted successfully!");
      fetchScheduleDetails();
    } catch (err) {
      console.error("Error deleting schedule:", err);
      alert("Failed to delete schedule!");
    }
  };

  const columns = [
    { field: "valuation_schedule_master_id", headerName: "ID", width: 90 },
    { field: "valuation_type", headerName: "Type", width: 120 },
    { field: "revaluation_round", headerName: "Round", width: 100 },
    { field: "valuation_from_date", headerName: "Start Date", width: 150 },
    { field: "valuation_to_date", headerName: "End Date", width: 150 },
    { field: "bundle_no_from", headerName: "Bundle From", width: 130 },
    { field: "bundle_no_to", headerName: "Bundle To", width: 130 },
    { field: "script_allot_size", headerName: "Allot Size", width: 130 },
    { field: "workflow_status", headerName: "Status", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => {
        if (params.row.workflow_status === "DRAFT") {
          return (
            <>
              <IconButton color="primary" onClick={() => handleEdit(params.row)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => handleDelete(params.row.valuation_schedule_master_id)}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </>
          );
        }
        return null;
      },
    },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Valuation Schedule – {row.exam_type_name} ({row.exam_month})
      </DialogTitle>
      <DialogContent>
        <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
          {editId ? "Edit Valuation Schedule" : "Add New Valuation Schedule"}
        </Typography>
        <Grid container spacing={2}>
          {/* ... same form fields as before ... */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Valuation Type"
              name="valuation_type"
              select
              fullWidth
              value={form.valuation_type}
              onChange={(e) => setForm({ ...form, valuation_type: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="NORMAL">NORMAL</option>
              <option value="REVALUATION">REVALUATION</option>
              <option value="RECOUNTING">RECOUNTING</option>
              <option value="CHALLENGE">CHALLENGE</option>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Revaluation Round(default 0)"
              type="number"
              name="revaluation_round"
              value={form.revaluation_round}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Valuation Pattern ID"
              type="number"
              name="valuation_pattern_id"
              value={form.valuation_pattern_id}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              name="valuation_from_date"
              value={form.valuation_from_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              name="valuation_to_date"
              value={form.valuation_to_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Bundle No From"
              name="bundle_no_from"
              value={form.bundle_no_from}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Bundle No To"
              name="bundle_no_to"
              value={form.bundle_no_to}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Script Allot Size"
              name="script_allot_size"
              value={form.script_allot_size}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Workflow Status"
              name="workflow_status"
              select
              fullWidth
              value={form.workflow_status}
              onChange={(e) => setForm({ ...form, workflow_status: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="DRAFT">DRAFT</option>
              <option value="APPROVED">APPROVED</option>
              <option value="PUBLISHED">PUBLISHED</option>
              <option value="CLOSED">CLOSED</option>
            </TextField>
          </Grid>
        </Grid>

        <Button
          onClick={handleAddOrUpdateSchedule}
          variant="contained"
          color="success"
          sx={{ mt: 2 }}
        >
          {editId ? "Update Schedule" : "Add Schedule"}
        </Button>

        <Divider sx={{ mt: 3, mb: 2 }} />

        <Typography variant="h6" sx={{ mb: 1 }}>
          Existing Schedules
        </Typography>
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={scheduleDetails}
            columns={columns}
            getRowId={(row) => row.valuation_schedule_master_id}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            disableColumnMenu
            onCellClick={(params, event) => event.stopPropagation()}
            onRowClick={(params, event) => event.stopPropagation()}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

ValuationScheduleDialog.propTypes = {
  row: PropTypes.shape({
    college_id: PropTypes.number,
    exam_schedule_master_id: PropTypes.number,
    exam_type_name: PropTypes.string,
    exam_month: PropTypes.string,
    valuation_schedule_master_id: PropTypes.number,
    valuation_type: PropTypes.string,
    revaluation_round: PropTypes.number,
    valuation_pattern_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    valuation_from_date: PropTypes.string,
    valuation_to_date: PropTypes.string,
    bundle_no_from: PropTypes.number,
    bundle_no_to: PropTypes.number,
    script_allot_size: PropTypes.number,
    workflow_status: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  examScheduleMasterId: PropTypes.number,
  examDate: PropTypes.string,
  programId: PropTypes.number,
  branchId: PropTypes.number,
  showSnackbar: PropTypes.func,
};

export default ValuationScheduleDialog;
